import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Avatar,
  Chip,
  Grid,
  Divider,
} from '@mui/material';
import { personsAPI } from '../../services/api';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

interface PersonComparisonViewProps {
  target: any;
  sources: any[];
}

const PersonComparisonView: React.FC<PersonComparisonViewProps> = ({
  target,
  sources,
}) => {
  const getInitials = (name: string) => {
    return name
      ?.split(' ')
      .map((n: string) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?';
  };

  const PersonCard: React.FC<{ person: any; label: string; color?: string }> = ({
    person,
    label,
    color = 'primary',
  }) => (
    <Paper
      sx={{
        p: 2,
        border: 2,
        borderColor: `${color}.main`,
        bgcolor: `${color}.50`,
      }}
    >
      <Stack spacing={1}>
        <Typography variant="caption" color="text.secondary" fontWeight="bold">
          {label}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar
            src={person.profilePhotoUrl ? personsAPI.getProfilePhotoUrl(person.id) : undefined}
            sx={{ width: 40, height: 40, bgcolor: `${color}.main` }}
          >
            {getInitials(person.name)}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {person.name || '—'}
            </Typography>
            {person.email && (
              <Typography variant="body2" color="text.secondary">
                {person.email}
              </Typography>
            )}
          </Box>
        </Stack>
        <Divider />
        <Grid container spacing={1}>
          {person.phone && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Phone:</strong> {person.phone}
              </Typography>
            </Grid>
          )}
          {person.nationality && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Nationality:</strong> {person.nationality}
              </Typography>
            </Grid>
          )}
          {person.address && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Address:</strong> {person.address}
              </Typography>
            </Grid>
          )}
          {person.types && person.types.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                {person.types.map((type: string) => (
                  <Chip key={type} label={type} size="small" variant="outlined" />
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </Stack>
    </Paper>
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Comparison
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={5}>
          <PersonCard person={target} label="Target (Keep)" color="primary" />
        </Grid>
        <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'center' }}>
          <ArrowForwardIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
        </Grid>
        <Grid item xs={12} md={5}>
          {sources.length === 1 ? (
            <PersonCard person={sources[0]} label="Source (Merge)" color="secondary" />
          ) : (
            <Paper sx={{ p: 2, border: 2, borderColor: 'secondary.main', bgcolor: 'secondary.50' }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" gutterBottom>
                Sources (Merge) - {sources.length} persons
              </Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {sources.map((source, idx) => (
                  <Box key={source.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar
                        src={source.profilePhotoUrl ? personsAPI.getProfilePhotoUrl(source.id) : undefined}
                        sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}
                      >
                        {getInitials(source.name)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {source.name}
                        </Typography>
                        {source.email && (
                          <Typography variant="caption" color="text.secondary">
                            {source.email}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    {idx < sources.length - 1 && <Divider sx={{ mt: 1 }} />}
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PersonComparisonView;

